import type { TOC } from '@ember/component/template-only';
import Text from './text';
import type { ComponentLike } from '@glint/template';
import { getIconTextClass } from 'tio-reimagined/color-tokens';
import type { ReimaginedColor } from 'tio-reimagined/color-tokens';

// ---------------------------------------------------------------------------
// SectionPanel – card container with icon header
// ---------------------------------------------------------------------------

/**
 * Color theme for the icon
 */
export type SectionPanelIconColor = ReimaginedColor;

interface SectionPanelSignature {
  Args: {
    /** Phosphor icon component to display */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Title text for the header */
    title: string;
    /** Color for the icon */
    iconColor?: SectionPanelIconColor;
    /** Icon size (default: 20) */
    iconSize?: number;
    /** Whether to use rounded-xl (true) or rounded-lg (false). Default: true */
    roundedXl?: boolean;
  };
  Blocks: {
    /** Optional content on the right side of the header (e.g., badge, button) */
    trailing?: [];
    /** Main body content */
    body: [];
  };
  Element: HTMLDivElement;
}

const getIconColorClass = (color?: SectionPanelIconColor) => getIconTextClass(color);

/**
 * SectionPanel - A card container with icon header
 *
 * Displays a rounded card with icon + title header and body content.
 * Commonly used throughout dashboards for consistent panel layouts.
 *
 * @example Basic usage
 * ```gts
 * import SectionPanel from 'tio-reimagined/components/section-panel';
 *
 * <SectionPanel @icon={{PhUsers}} @title="Team Members" @iconColor="violet">
 *   <:body>
 *     <p>Panel content here</p>
 *   </:body>
 * </SectionPanel>
 * ```
 *
 * @example With trailing badge
 * ```gts
 * <SectionPanel @icon={{PhQueue}} @title="Pending Approvals" @iconColor="violet">
 *   <:trailing>
 *     <Chip @intent="warning" @size="sm">5</Chip>
 *   </:trailing>
 *   <:body>
 *     <p>Approval list here</p>
 *   </:body>
 * </SectionPanel>
 * ```
 */
const SectionPanel: TOC<SectionPanelSignature> = <template>
  <div
    data-test-section-panel
    class="bg-white {{if @roundedXl 'rounded-xl' 'rounded-lg'}} border border-gray-200"
    ...attributes
  >
    <div
      data-test-section-panel-header
      class="flex items-center justify-between gap-2 p-5 border-b border-gray-100"
    >
      <div class="flex items-center gap-2">
        {{#let @icon as |Icon|}}
          <Icon @size={{if @iconSize @iconSize 20}} class={{getIconColorClass @iconColor}} />
        {{/let}}
        <Text class="font-semibold text-gray-900">{{@title}}</Text>
      </div>
      {{#if (has-block "trailing")}}
        <div data-test-section-panel-trailing>
          {{yield to="trailing"}}
        </div>
      {{/if}}
    </div>
    <div data-test-section-panel-body class="p-5">
      {{yield to="body"}}
    </div>
  </div>
</template>;

export default SectionPanel;

export type { SectionPanelSignature };
